import { Button, Flex, Text, theme } from '@/components';
import type { graphTypeEnum } from '@/features/analysis/_models/project';
import {
  LABEL_MAX_LENGTH,
  getGraphLabel,
  tooltipText,
} from '@/features/analysis/_utils';
import { useLocaledAmount } from '@/providers';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import { type FC, type RefObject, useMemo } from 'react';
import type { z } from 'zod';
import { useChartVisibility } from '../../../_hooks/useChartVisibility';

type Legend = {
  name: string;
  color?: string;
  id: string | null;
  enableDrillDown: boolean;
  onClick: () => void;
};

type Props = {
  highchartsRef: RefObject<HighchartsReactRefObject | null>;
  legendData: Legend[];
  graphType: z.infer<typeof graphTypeEnum>;
  /** useChartVisibility 用。RS / KS で衝突しないようキーを分ける */
  visibilityStorageKey?: string;
  /** useChartVisibility 用。検索条件変更時に表示状態をリセットしたい場合に渡す */
  visibilitySearchQueryStates?: Record<string, unknown>;
};

export const CustomLegend: FC<Props> = ({
  highchartsRef,
  legendData,
  graphType,
  visibilityStorageKey,
  visibilitySearchQueryStates,
}) => {
  const { token } = theme.useToken();
  const { unit } = useLocaledAmount();

  const isPieOrTreeMapGraph =
    graphType === 'pieChart' || graphType === 'treeMap';

  const chart = highchartsRef.current?.chart;
  const series = useMemo(
    () => (chart && 'series' in chart ? chart.series : []),
    [chart],
  );

  // カスタムフックで表示/非表示の状態管理を統一
  const { hiddenNodes, toggleVisibility, isHidden, getLegendIdentifier } =
    useChartVisibility({
      highchartsRef,
      legendData,
      graphType,
      storageKey: visibilityStorageKey,
      searchQueryStates: visibilitySearchQueryStates,
    });

  const handlePieOrTreeMapChartLegendHover = (legendIdentifier: string) => {
    if (!series[0]) {
      return;
    }
    const selectedPoint = series[0].points.find(
      (point) => (point.options.custom?.id ?? point.name) === legendIdentifier,
    );
    if (chart && selectedPoint) {
      const labelText =
        selectedPoint.name.length > LABEL_MAX_LENGTH
          ? `${selectedPoint.name.substring(0, LABEL_MAX_LENGTH)}...`
          : selectedPoint.name;

      const labelForDimensions = getGraphLabel({
        point: selectedPoint,
        chart,
        unit,
        options: {
          labelHTML: `<div>
            <span style="color: ${selectedPoint.color};">●</span>
            <span>${labelText}</span> :<b>${tooltipText(unit, selectedPoint.y)}</b>
          </div>`,
          attributes: { fill: token.colorBgBase },
        },
      });

      labelForDimensions.destroy();
    }
  };

  const handleSeriesActive = (legendIdentifier?: string) => {
    const selectedNode =
      isPieOrTreeMapGraph && series[0]
        ? series[0].points.filter(
            (point) =>
              (point.options.custom?.id ?? point.name) !== legendIdentifier,
          )
        : series.filter((item) => item.options.custom?.id !== legendIdentifier);

    for (const series of selectedNode) {
      if (legendIdentifier) {
        series.setState('inactive', true);
      } else {
        series.setState('normal');
      }
    }
  };

  const updateSeriesBorder = (
    legendIdentifier: string,
    showBorder?: boolean,
  ) => {
    if (isPieOrTreeMapGraph && series[0]) {
      const selectedPoint = series[0].points.find(
        (point) =>
          (point.options.custom?.id ?? point.name) === legendIdentifier,
      );
      selectedPoint?.graphic?.attr({
        'stroke-width': showBorder ? 1 : 0,
        stroke: showBorder ? token.colorBgSpotlight : token.colorBgBase,
      });
    } else {
      const points = series.find(
        (item) => (item.options.custom?.id ?? item.name) === legendIdentifier,
      )?.points;

      for (const point of points ?? []) {
        if (showBorder) {
          point.setState('hover', true);
        } else {
          point.setState('normal');
        }
      }
    }
  };

  const handleLegendClick = (legendIdentifier: string) => {
    toggleVisibility(legendIdentifier);
  };

  const handleMouseEnter = (legendIdentifier: string) => {
    if (!hiddenNodes.includes(legendIdentifier)) {
      if (isPieOrTreeMapGraph) {
        handlePieOrTreeMapChartLegendHover(legendIdentifier);
      }
      handleSeriesActive(legendIdentifier);
      updateSeriesBorder(legendIdentifier, true);
    }
  };

  const handleMouseLeave = (legendIdentifier: string) => {
    handleSeriesActive();
    updateSeriesBorder(legendIdentifier);
  };

  return (
    <Flex
      vertical
      gap="small"
      style={{
        maxHeight: 350,
        overflowY: 'auto',
        width: '20%',
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: 16,
        borderRadius: 4,
      }}
    >
      {legendData.map((legend, idx) => {
        const legendIdentifier = getLegendIdentifier(legend);
        const hidden = isHidden(legendIdentifier);
        const enableDrillDown = legend.enableDrillDown && !hidden;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
          <Flex key={idx} gap="small" justify="space-between">
            <Flex
              gap="small"
              align="center"
              style={{
                border: 1,
                borderStyle: 'solid',
                borderColor: hidden ? 'transparent' : token.colorBorder,
                borderRadius: 14,
                width: 'fit-content',
                maxWidth: '80%',
                padding: '4px 8px',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (enableDrillDown) {
                  legend.onClick();
                }
              }}
              onMouseEnter={() => {
                handleMouseEnter(legendIdentifier);
              }}
              onMouseLeave={() => {
                handleMouseLeave(legendIdentifier);
              }}
              onTouchStart={() => {
                handleMouseEnter(legendIdentifier);
              }}
              onTouchEnd={() => {
                handleMouseLeave(legendIdentifier);
              }}
              onTouchCancel={() => {
                handleMouseLeave(legendIdentifier);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                handleMouseLeave(legendIdentifier);
              }}
            >
              <span
                style={{
                  display: 'inline-grid',
                  width: 10,
                  height: 10,
                  borderRadius: 1000,
                  borderStyle: 'solid',
                  borderWidth: 2,
                  borderColor: legend.color,
                  backgroundColor: legend.color,
                  ...(hidden && {
                    borderColor: 'transparent',
                    backgroundColor: token.colorTextSecondary,
                  }),
                }}
              />
              <Text
                weight="regular"
                variant="body"
                color={hidden ? 'secondary' : 'normal'}
                style={{ whiteSpace: 'nowrap' }}
                ellipsis={{
                  tooltip: {
                    styles: {
                      root: { maxWidth: 200, whiteSpace: 'pre-wrap' },
                    },
                  },
                }}
                text={legend.name}
              />
              {enableDrillDown && <RightOutlined />}
            </Flex>
            <Button
              type="text"
              onClick={() => {
                handleLegendClick(legendIdentifier);
                handleMouseLeave(legendIdentifier);
              }}
              icon={
                hidden ? (
                  <EyeInvisibleOutlined style={{ fontSize: 16 }} />
                ) : (
                  <EyeOutlined style={{ fontSize: 16 }} />
                )
              }
            />
          </Flex>
        );
      })}
    </Flex>
  );
};
